import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageWrapper from '../components/PageWrapper';
import axiosInstance from '../axiosInstance';
import { LanguageContext } from '../contexts/LanguageContext';
import { ToastContainer, toast } from 'react-toastify';

// --- Extracted GameCard Component ---
const GameCard = React.memo(({ title, winAmount, cost, imageUrl, altText, onClick, comingSoon = false, type, t }) => {
  let buttonClasses = '';
  let buttonTextColor = 'text-white';

  if (comingSoon) {
    buttonClasses = 'bg-gray-500 text-gray-300 cursor-not-allowed';
  } else {
    switch (type) {
      case 'bingo':
        buttonClasses = 'bg-[#FF6347] hover:bg-[#e05030]';
        buttonTextColor = 'text-white';
        break;
      case 'money_bag':
        buttonClasses = 'bg-yellow-600 hover:bg-yellow-700';
        buttonTextColor = 'text-white';
        break;
      case 'diamond':
        buttonClasses = 'bg-blue-600 hover:bg-blue-700';
        buttonTextColor = 'text-white';
        break;
      case 'keno':
        buttonClasses = 'bg-purple-600 hover:bg-purple-700';
        buttonTextColor = 'text-white';
        break;
      default:
        buttonClasses = 'bg-[#F49C00] hover:bg-[#e08f00] text-white';
    }
  }

  return (
    <div
      className={`relative rounded-xl shadow-2xl overflow-hidden flex flex-col items-center justify-between p-4 transform transition-transform duration-300 hover:scale-105 border border-gray-200
        ${comingSoon ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
      style={{
        width: '280px',
        height: '400px',
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
      }}
      onClick={!comingSoon ? onClick : null}
    >
      {comingSoon && (
        <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full rotate-12">
          {t('comingSoon')}
        </span>
      )}
      <div className="absolute inset-0 bg-black bg-opacity-30 rounded-xl"></div>

      <div className="relative z-10 flex flex-col items-center justify-between h-full w-full">
        <div className="text-sm font-semibold text-white self-start mb-2 bg-black bg-opacity-50 px-2 py-1 rounded-md">
          {cost ? `${cost} ${t('birr')}` : ''}
        </div>

        <div className="text-center w-full mt-auto">
          <h3 className="text-3xl font-bold text-white mb-1 drop-shadow-lg">{title}</h3>
          <p className="text-xl font-semibold text-yellow-300 mb-4 drop-shadow-lg">
            {t('win')} {winAmount} {t('birr')}
          </p>
          <button
            className={`w-full py-4 rounded-lg font-bold text-xl transition duration-300 ${buttonClasses} ${buttonTextColor}`}
            onClick={!comingSoon ? onClick : null}
            disabled={comingSoon}
            type="button"
          >
            {t('play')}
          </button>
        </div>
      </div>
    </div>
  );
});

// --- DashboardPage Component ---
const DashboardPage = (props) => {
  const { language } = useContext(LanguageContext);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Detect if running inside Telegram WebApp
  const isTelegramWebApp = useMemo(() => !!window.Telegram?.WebApp, []);

  // Translations with fallback to English
  const translations = useMemo(() => ({
    en: {
      loadingDashboard: "Loading dashboard...",
      sessionExpired: "Your session has expired or is invalid. Redirecting to login...",
      networkError: "Network error or server unreachable. Redirecting to login...",
      fetchError: "Network or server error while fetching user data.",
      goToLogin: "Go to Login",
      heyPlayer: "HEY, PLAYER",
      guest: "Guest",
      balance: "Balance",
      bonus: "Bonus",
      bingoGame: "Bingo",
      fastKeno: "Fast Keno",
      play: "Play Now",
      comingSoon: "Coming Soon",
      scratchCard: "Scratch Card",
      birr: "Birr",
      userAvatar: "User Avatar",
      bingoBalls: "Bingo Balls",
      moneyBag: "Money Bag",
      diamond: "Diamond",
      win: "WIN",
      smallScratchBigWins: "Small scratch, big wins!",
      playInstantGames: "Play Instant Games",
      scratchToReveal: "Scratch to Reveal!",
      youWon: "You Won: {amount} Birr!",
      betterLuckNextTime: "Better Luck Next Time!",
      playAgain: "Play Again",
      goBack: "Go Back to Dashboard",
      scratchArea: "Scratch Area",
      prizeArea: "Prize Area",
      insufficientBalance: "Insufficient balance to play. Please top up.",
      gameError: "An error occurred during the game. Please try again.",
      processing: "Processing...",
      scratchToPlay: "Scratch to play for {cost} Birr!",
      winAmount: "Win {amount} Birr!",
    },
    am: {
      loadingDashboard: "ዳሽቦርድ በመጫን ላይ...",
      sessionExpired: "የእርስዎ ክፍለ ጊዜ አልቋል ወይም የማያገለግል ነው። ወደ መግቢያ በመቀየር ላይ...",
      networkError: "የኔትወርክ ስህተት ወይም አገልጋዩ አልተገኘም። ወደ መግቢያ በመቀየር ላይ...",
      fetchError: "የኔትወርክ ወይም የሰርቨር ስህተት የተጠቃሚ ዳታ በሚገኝበት ጊዜ።",
      goToLogin: "ወደ መግቢያ ይሂዱ",
      heyPlayer: "ሄይ ፣ ተጫዋች",
      guest: "እንግዳ",
      balance: "ሒሳብ",
      bonus: "ቦነስ",
      bingoGame: "ቢንጎ",
      fastKeno: "ፈጣን ኬኖ",
      play: "አሁን ይጫወቱ",
      comingSoon: "በቅርቡ ይመጣል",
      scratchCard: "ከሽከሽ እጣ",
      birr: "ብር",
      userAvatar: "የተጠቃሚ ምስል",
      bingoBalls: "ቢንጎ ኳሶች",
      moneyBag: "የገንዘብ ቦርሳ",
      diamond: "አልማዝ",
      win: "ያሸንፉ",
      smallScratchBigWins: "ትንሽ ከሽክሽ፣ ትልቅ ድሎች!",
      playInstantGames: "ፈጣን ጨዋታዎችን ይጫወቱ",
      scratchToReveal: "ለመግለጥ ይከሽክሹ!",
      youWon: "አሸንፈዋል: {amount} ብር!",
      betterLuckNextTime: "በሚቀጥለው ጊዜ መልካም ዕድል!",
      playAgain: "እንደገና ይጫወቱ",
      goBack: "ወደ ዳሽቦርድ ይመለሱ",
      scratchArea: "የመከሽከሻ ቦታ",
      prizeArea: "የሽልማት ቦታ",
      insufficientBalance: "መጫወት የማይችል በቂ ሒሳብ የለም። እባክዎ ይሙሉ.",
      gameError: "በጨዋታው ወቅት ስህተት ተከስቷል። እባክዎ እንደገና ይሞክሩ።",
      processing: "በማስኬድ ላይ...",
      scratchToPlay: "ለመጫወት {cost} ብር ይከሽክሹ!",
      winAmount: "{amount} ብር ያሸንፉ!",
    },
    or: {
      loadingDashboard: "Daashboordii ni fe'ama...",
      sessionExpired: "Yeroo keessan xumurame ykn hin fayyadne. Seensa irratti deebi'uun jira...",
      networkError: "Dogoggora nettoworkii ykn serverii hin argamne. Seensa irratti deebi'uun jira...",
      fetchError: "Dogoggora nettoworkii ykn serverii data fayyadamaa argachuuf.",
      goToLogin: "Seensa seenaa",
      heyPlayer: "AKKA, TAPHATAA",
      guest: "Daawwitii",
      balance: "Baalaansii",
      bonus: "Boonsii",
      bingoGame: "Bingo",
      fastKeno: "Keno Saffisaa",
      play: "Amma Taphadhu",
      comingSoon: "Fuula duraa",
      scratchCard: "Kaardii Qaawwaa",
      birr: "Birr",
      userAvatar: "Suuraa fayyadamaa",
      bingoBalls: "Baalota Bingo",
      moneyBag: "Baajii Maallaqaa",
      diamond: "Dayimandii",
      win: "MOO'ADHU",
      smallScratchBigWins: "Qaawwaa xiqqaa, moo'annoo gurguddaa!",
      playInstantGames: "Taphoota Battalaa Taphadhu",
      scratchToReveal: "Saaxiluuf qaawwaa!",
      youWon: "Moo'atte: {amount} Birr!",
      betterLuckNextTime: "Yeroo itti aanutti carraa gaarii!",
      playAgain: "Deebii taphadhu",
      goBack: "Gara Daashboordii deebi'i",
      scratchArea: "Iddoo qaawwaa",
      prizeArea: "Iddoo badhaasaa",
      insufficientBalance: "Baalaansiin gahaa hin jiru. Guuti.",
      gameError: "Dogoggorri tapha keessatti uumameera. Irra deebi'ii yaali.",
      processing: "Hojjechaa jira...",
      scratchToPlay: "Taphachuuf Birr {cost} qaawwaa!",
      winAmount: "Birr {amount} moo'adhu!",
    },
    ti: {
      loadingDashboard: "ዳሽቦርድ እዚ ዝተሳተፈ እዩ...",
      sessionExpired: "ናይ እርስዎ ክፍለ ጊዜ አልቋል ወይ ዘይሕጋዊ እዩ። ናብ መእተዊ እዩ ዝውሰድ...",
      networkError: "ናብ መእተዊ እዩ ዝውሰድ ዘይተረኽበ ኔትዎርክ ወይ ሰርቨር...",
      fetchError: "ናብ መእተዊ እዩ ዝውሰድ ዘይተረኽበ ኔትዎርክ ወይ ሰርቨር ዳታ...",
      goToLogin: "ናብ መእተዊ ኪድ",
      heyPlayer: "ሄይ ፣ ተጫዋች",
      guest: "ጎብኚ",
      balance: "ሒሳብ",
      bonus: "ቦነስ",
      bingoGame: "ቢንጎ",
      fastKeno: "ቅልጡፍ ኬኖ",
      play: "ሕጂ ተጻወት",
      comingSoon: "ብቕልጡፍ ክመጽእ",
      scratchCard: "ከሽከሽ እጣ",
      birr: "ብር",
      userAvatar: "ምስሊ ተጠቃሚ",
      bingoBalls: "ኳሳት ቢንጎ",
      moneyBag: "ናይ ገንዘብ ከረጢት",
      diamond: "ኣልማዝ",
      win: "ዕወት",
      smallScratchBigWins: "ንእሽቶ ከሽከሽ፣ ዓብዪ ዓወታት!",
      playInstantGames: "ቅልጡፍ ጸወታታት ተጻወት",
      scratchToReveal: "ንምግላጽ ከሽክሽ!",
      youWon: "ኣዕዊትካ: {amount} ብር!",
      betterLuckNextTime: "ዝሓሸ ዕድል ብዝቕጽል ግዜ!",
      playAgain: "እንደገና ተጻወት",
      goBack: "ናብ ዳሽቦርድ ተመለስ",
      scratchArea: "ከሽከሽ ቦታ",
      prizeArea: "ሽልማት ቦታ",
      insufficientBalance: "ናይ ምጽዋት ዝኽእል በቂ ዝኾነ ሒሳብ የብልካን። እባኻ ምልእ።",
      gameError: "ስሕተት ኣብ ጸወታ ኣጋጢሙ። እባኻ እንደገና ሞክር።",
      processing: "ምስራሕ...",
      scratchToPlay: "ንኽትጻወት {cost} ብር ፍሓቕ!",
      winAmount: "{amount} ብር ተዓወት!",
    },
    wo: {
      loadingDashboard: "Daashboordii bolla hayyo...",
      sessionExpired: "Sooraada kee guutuu deebi’aa. Gara seensaatti galee yaali...",
      networkError: "Rakkoo networkii yookiin server hin argamne. Gara seensaatti deebi’aa...",
      fetchError: "Rakkoo networkii yookiin server irraa data fiduu dadhabe.",
      goToLogin: "Seensa dhaqaa",
      heyPlayer: "HEY, TAPHAATAA",
      guest: "Bariisaa",
      balance: "Baalaansii",
      bonus: "Boonsii",
      bingoGame: "Bingo",
      fastKeno: "Keno Saffisaa",
      play: "Amma Taphadhu",
      comingSoon: "Duraa dhufa",
      scratchCard: "Kaardii Qooqa",
      birr: "Birr",
      userAvatar: "Suuraa Fayyadamaa",
      bingoBalls: "Kubboo Bingo",
      moneyBag: "Koboo Maallaqaa",
      diamond: "Almaazi",
      win: "MO’AA",
      smallScratchBigWins: "Qooqa xiqqoo, mo’annoo guddaa!",
      playInstantGames: "Taphoota Saffisoo Taphadhu",
      scratchToReveal: "Dubbisuuf Qooqa!",
      youWon: "Ati moofte: {amount} Birr!",
      betterLuckNextTime: "Carraan gaariin itti aanutti!",
      playAgain: "Deemmoo taay",
      goBack: "Deebi’ee Gara Daashboordii",
      scratchArea: "Iddoo Qooqa",
      prizeArea: "Iddoo Badhaasaa",
      insufficientBalance: "Baalaansi gahaa hin jiru. Maallaqa dabalataa galchi.",
      gameError: "Rakkoon taphichaa mudate. Dabalataan yaali.",
      processing: "Hojjechaa jira...",
      scratchToPlay: "Taphachuuf {cost} Birr qooqa!",
      winAmount: "Mo’ii {amount} Birr!",
    },
  }), []);

  // Translation function with fallback to English
  const t = useCallback(
    (key, replacements = {}) => {
      const text = (translations[language] && translations[language][key]) || translations.en[key] || key;
      return Object.entries(replacements).reduce(
        (str, [k, v]) => str.replace(new RegExp(`{${k}}`, 'g'), v),
        text
      );
    },
    [language, translations]
  );

  // Toast helper
  const showToast = useCallback(
    (message, type = 'error') => {
      if (type === 'error') toast.error(message, { position: 'top-center' });
      else if (type === 'success') toast.success(message, { position: 'top-center' });
      else if (type === 'info') toast.info(message, { position: 'top-center' });
    },
    []
  );

  // Handle Scratch Card clicks
  const handleScratchCardClick = useCallback((gamePath) => {
    navigate(gamePath);
  }, [navigate]);

  // Fetch user profile
  const fetchUserProfile = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/user/profile');
      setUserData(response.data);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        showToast(t('sessionExpired'), 'error');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else if (err.message && err.message.includes('Network Error')) {
        showToast(t('networkError'), 'error');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(t('fetchError'));
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, showToast, t]);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  if (loading) {
    return (
      <PageWrapper {...props}>
        <div className="flex justify-center items-center h-screen">
          <p className="text-gray-700 text-lg">{t('loadingDashboard')}</p>
        </div>
      </PageWrapper>
    );
  }

  // Handle Bingo click with Telegram WebApp detection
  const handleBingoClick = () => {
    if (isTelegramWebApp) {
      toast.info('Please click the Launch button in the Telegram app to start the Bingo game.', {
        position: 'top-center',
        autoClose: 6000,
        closeOnClick: true,
        pauseOnHover: true,
      });
      return;
    }
    navigate('/stake-selection');
  };

  return (
    <PageWrapper {...props}>
      <ToastContainer />
      <div
        className="relative w-full min-h-[calc(100vh-4rem)] bg-cover bg-center bg-no-repeat py-8"
        style={{
          backgroundImage: "url('./bg-pattern.png')",
        }}
      >
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-white flex items-center">
              <img src="./icons/game-controller.png" alt="Games Icon" className="mr-3 w-8 h-8" />
              {t('playInstantGames')}
            </h2>
            <p className="text-gray-300 text-sm">{t('smallScratchBigWins')}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 justify-items-center">
            {/* NEW: Fast Keno Game Card */}
            <GameCard
              title={t('fastKeno')}
              winAmount="Variable"
              cost="Variable"
              imageUrl="./icons/keno-ball-1.png"
              altText={t('fastKeno')}
              onClick={() => navigate('/fast-keno')}
              type="keno"
              comingSoon={false}
              t={t}
            />
            
            <GameCard
              title={t('bingoGame')}
              winAmount="Variable"
              cost="Variable"
              imageUrl="./icons/bingo_balls.png"
              altText={t('bingoGame')}
              onClick={handleBingoClick}
              type="bingo"
              comingSoon={false}
              t={t}
            />

            <GameCard
              title={t('moneyBag')}
              winAmount="Variable"
              cost="5"
              imageUrl="./icons/money_bag.png"
              altText={t('moneyBag')}
              onClick={() => handleScratchCardClick('/money-bag-game')}
              type="money_bag"
              comingSoon={false}
              t={t}
            />

            <GameCard
              title={t('diamond')}
              winAmount="Variable"
              cost="10"
              imageUrl="./icons/diamond.png"
              altText={t('diamond')}
              onClick={() => handleScratchCardClick('/diamond-game')}
              type="diamond"
              comingSoon={false}
              t={t}
            />
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default DashboardPage;